import { useContext } from 'react';
import { store } from 'components/Store';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import dayjs from 'dayjs';
import uppercaseFirstLetter from 'components/Dashboard/utilities/uppercaseFirstLetter';
import { RegionMetaData } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/PartyMetadata';
import { chartDataOptions, createIncludeRepostsOption } from './createAnalyticsState';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import { sortPositionLabels } from './sortPositionLabels';
import useSelectedCountriesFilters from './useSelectedCountriesFilters';
import useAnalyticsIntervalLimit from './useAnalyticsIntervalLimit';
import { useAuth0 } from '@auth0/auth0-react';
import ChangeSearchQuery from 'components/Dashboard/utilities/changeSearchQuery';
import getAppSite from 'utils/getAppSite';
import graphDataSections from 'components/Dashboard/Analytics/graphDataSections';
import useAnalyticsCommonHelpers from 'components/Dashboard/Analytics/hooks/useAnalyticsCommonHelpers';

const colours = {
  positive: '163, 228, 86',
  negative: '227, 66, 66',
  neutral: '212, 212, 212',
  mixed: '195, 147, 76',
};

const useCallOverallSentiment = (props) => {
  const { teamId, reportDateInterval, selectedCountriesProps } = props ?? {};
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists, analyticsState, activePlan } = state;
  const { chartDataOptionSelected } = analyticsState;
  const { isAuthenticated } = useAuth0();
  const { enableTwitter } = activePlan ?? {};
  const keywordsListsToUse = keywordsLists.filter((item) => item.id !== null);
  const { createFilters, createKey, TweetsFilters } = useAnalyticsCommonHelpers();

  const { getCountrySpecificFilters, getWrittenQuestionCountryFilters, UKSelected } = useSelectedCountriesFilters({
    selectedCountriesProps,
  });

  const { generalApiCall } = useGeneralApiCall();
  const { validateDataForLoggedOutUsers, conditionOfPublicApi } = useAnalyticsIntervalLimit();
  const topicParameter = getUrlParam('topic-id');
  const createDataInterval = () => {
    return dayjs().subtract(import.meta.env.VITE_APP_SENTIMENT_SNAPSHOT_NUM_DAYS ?? 90, 'day');
  };
  const parliamentaryPositionsFilters =
    chartDataOptions.find((item) => item.name === 'Parliamentary position')?.filters ?? [];

  const createDateIntervalFilters = (dateInterval) => {
    if (reportDateInterval) {
      return [...createFilters(reportDateInterval)];
    } else {
      const dateIntervalToUse = dateInterval ?? createDataInterval().startOf('day').format('YYYY-MM-DD');
      return [
        {
          field: 'dateTime',
          value: dateIntervalToUse,
          operator: 'date_gte',
        },
        {
          field: 'dateTime',
          value: dayjs().format('YYYY-MM-DD'),
          operator: 'date_lt',
        },
      ];
    }
  };

  const individualApiCall = async ({
    topic,
    section,
    source,
    freeSearch,
    stakeholderId,
    chartDataOption,
    dateInterval,
  }) => {
    const { value, field, type, additionalFilters } = section;
    const pathname = `/api/${teamId ? 'widget' : 'graph-data'}/${
      freeSearch && conditionOfPublicApi ? 'public-' : ''
    }aggregation${!!topic && !topic?.prospective && !freeSearch ? '-by-keyword-list' : ''}`;
    const method = 'post';
    let filters = [...createDateIntervalFilters(dateInterval)];

    if (type?.toLowerCase() === 'tweet') {
      // Handle tweets separately - add TweetsFilters and isRetweet filter
      if (!stakeholderId) {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      if (additionalFilters) {
        filters = [...filters, ...additionalFilters];
      }
      if (
        chartDataOption === 'Contribution type' ||
        (chartDataOption === 'Overall' &&
          !createIncludeRepostsOption({
            tweetsDateInterval: { filter: [createDataInterval().startOf('day').format('YYYY-MM-DD')] },
          }))
      ) {
        filters = [
          ...filters,
          {
            field: 'isRetweet',
            value: value,
            operator: 'str_eq',
          },
        ];
      }
    } else if (getAppSite() === 'global') {
      if (!stakeholderId) {
        filters = [...filters, ...parliamentaryPositionsFilters];
      }
      if (type === 'WrittenQuestion') {
        if (!stakeholderId) {
          filters = [...filters, ...getWrittenQuestionCountryFilters()];
        }
      } else {
        filters = [
          ...filters,
          ...getCountrySpecificFilters({
            value: value,
            field: field,
          }),
        ];
      }
    } else if (getAppSite() === 'usa') {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'CREC',
          operator: 'str_eq',
        },
      ];
    }

    // Add additionalFilters for non-tweet types (tweets already have them added above)
    if (additionalFilters && type?.toLowerCase() !== 'tweet') {
      filters = [...filters, ...additionalFilters];
    }
    if (stakeholderId) {
      filters = [
        ...filters,
        {
          field:
            type?.toLowerCase() === 'tweet'
              ? 'contactId'
              : type === 'WrittenQuestion'
                ? 'questionByContactId'
                : 'contactIds',
          value: stakeholderId,
          operator: 'str_eq',
        },
      ];
    }

    let requestProperties = {
      type: type,
      filters,
      bucketField: 'sentiment',
      order: 'asc',
    };

    if (topic?.prospective) {
      const { keywords, andKeywords, keywordExclusions } = topic;
      requestProperties = {
        ...requestProperties,
        query: ChangeKeywordsCategory(keywords),
        andQueries: ChangeKeywordsExclusions(andKeywords),
        mustNotQueries: ChangeKeywordsExclusions(keywordExclusions),
      };
    } else if (freeSearch) {
      requestProperties = {
        ...requestProperties,
        query: ChangeSearchQuery(freeSearch),
      };
    } else {
      requestProperties = {
        ...requestProperties,
        keywordListId: topic.id,
      };
    }

    if (teamId) {
      requestProperties = {
        ...requestProperties,
        teamId,
      };
    }

    let results = await generalApiCall({
      pathname,
      method,
      requestProperties,
      needsAuthentication: !teamId || (freeSearch && isAuthenticated),
      notShowErrorMessage: true,
      requestSource: source,
      returnError: true,
    });
    if (!validateDataForLoggedOutUsers(results)) {
      return [];
    }
    if (!!results) {
      return results;
    }
  };

  const sentiments = ['positive', 'negative', 'neutral', 'mixed'];
  const types = [
    {
      type: 'ParliamentaryRecord',
      contentTypes: ['ParliamentaryContribution', 'QuestionAndAnswer', 'Edm', 'SeneddCommitteeTranscriptContribution'],
    },
  ];

  if (import.meta.env.VITE_APP_ENABLE_TWITTER === 'true' && enableTwitter) {
    types.push({ type: 'Tweet', additionalFilters: TweetsFilters });
  }

  const dataTableIndividualApiCalls = async ({
    topic,
    item,
    optionData,
    sentiment,
    source,
    freeSearch,
    dateInterval,
  }) => {
    const { type, contentTypes, additionalFilters, field, value } = item;
    let pathname = `/api/${teamId ? 'widget' : 'graph-data'}/${
      freeSearch && conditionOfPublicApi ? 'public-' : ''
    }aggregation${!!topic && !topic?.prospective && !freeSearch ? '-by-keyword-list' : ''}`;
    let method = 'post';
    let filters = [
      ...createDateIntervalFilters(dateInterval),
      {
        field: 'sentiment',
        value: sentiment,
        operator: 'str_eq',
      },
    ];

    if (getAppSite() === 'global') {
      filters = [...filters, ...parliamentaryPositionsFilters];
    } else if (getAppSite() === 'usa' && type?.toLowerCase() !== 'tweet') {
      filters = [
        ...filters,
        {
          field: 'contentType',
          value: 'CREC',
          operator: 'str_eq',
        },
      ];
    }

    if (optionData.filters) {
      filters = [...filters, ...optionData.filters];
    }

    if (contentTypes && getAppSite() === 'global') {
      contentTypes.forEach((item) => {
        filters = [...filters, ...getCountrySpecificFilters({ value: item, field: 'contentType' })];
      });
    }

    if (additionalFilters) {
      filters = [...filters, ...additionalFilters];
    }

    // Handle isRetweet filter for Tweets and Retweets
    if (type?.toLowerCase() === 'tweet' && field === 'isRetweet') {
      filters.push({
        field: 'isRetweet',
        value: value,
        operator: 'str_eq',
      });
    }

    let requestProperties = {
      type: type,
      filters,
      bucketField: optionData.bucketField,
      order: 'asc',
    };

    if (topic?.prospective) {
      const { keywords, andKeywords, keywordExclusions } = topic;
      requestProperties = {
        ...requestProperties,
        query: ChangeKeywordsCategory(keywords),
        andQueries: ChangeKeywordsExclusions(andKeywords),
        mustNotQueries: ChangeKeywordsExclusions(keywordExclusions),
      };
    } else if (freeSearch) {
      requestProperties = {
        ...requestProperties,
        query: ChangeSearchQuery(freeSearch),
      };
    } else {
      requestProperties = {
        ...requestProperties,
        keywordListId: topic.id,
      };
    }

    if (teamId) {
      requestProperties = {
        ...requestProperties,
        teamId,
      };
    }
    let results = await generalApiCall({
      pathname,
      method,
      requestProperties,
      needsAuthentication: true,
      notShowErrorMessage: true,
      requestSource: source,
      returnError: true,
    });

    if (!validateDataForLoggedOutUsers(results)) {
      return [];
    }

    if (!!results) {
      return results;
    }
  };

  const callOverallDataChart = async ({
    chartDataOption,
    topic,
    excludeMixedAndNeutral,
    source,
    freeSearch,
    dateInterval,
  }) => {
    let optionData = chartDataOptions.find((item) => item.name === chartDataOption);
    let finalResults = {};
    for (let i = 0; i < types.length; i++) {
      let item = types[i];
      let finalSentiments = excludeMixedAndNeutral ? ['positive', 'negative'] : sentiments;
      for (let a = 0; a < finalSentiments.length; a++) {
        let sentiment = finalSentiments[a];
        let results = await dataTableIndividualApiCalls({
          topic,
          optionData,
          item,
          sentiment,
          source,
          freeSearch,
          dateInterval,
        });
        if (results) {
          if (!finalResults[sentiment]) {
            finalResults[sentiment] = [];
          }
          finalResults[sentiment].push(results.values);
        }
      }
    }

    Object.keys(finalResults).forEach((result) => {
      let data = finalResults[result];
      let compiledResults = data.reduce((acc, item) => {
        let obj = item;
        Object.keys(obj).forEach((key) => {
          if (!acc[key]) {
            acc[key] = obj[key];
          } else {
            acc[key] += obj[key];
          }
        });
        return acc;
      }, {});
      finalResults[result] = compiledResults;
    });

    let value = Object.keys(finalResults).map((item) => {
      let results = 0;
      let content = finalResults[item];
      for (let i in content) {
        results += content[i];
      }
      return results;
    });

    let rawData = {};

    Object.keys(finalResults).forEach((item) => {
      let content = finalResults[item];
      let initialKeys = Object.keys(content);
      let orderingKeys = initialKeys;
      if (chartDataOption === 'Region') {
        orderingKeys = [];
        RegionMetaData.orderingRegions.forEach((item) => {
          if (initialKeys.includes(item.toLowerCase()) || item === 'Yorkshire and the Humber') {
            orderingKeys.push(item === 'Yorkshire and the Humber' ? 'yorkshire & the humber' : item.toLowerCase());
          }
        });
      }

      orderingKeys.forEach((key) => {
        let name = createKey({ chartDataOption, item: key });
        let condition = optionData.showRowsFor ? optionData.showRowsFor?.includes(name) : true;

        if (!rawData[name] && condition) {
          rawData[name] = {};
        }
        if (condition) {
          rawData[name][item] = content[key];
        }
      });
    });
    const sortedRawData = sortPositionLabels(Object.keys(rawData)).reduce((accumulator, key) => {
      accumulator[key] = rawData[key];
      return accumulator;
    }, {});
    if (value?.length > 0) {
      return { value, rawData: sortedRawData };
    }
  };

  const callOverallSentiment = async ({
    topic = topicParameter
      ? keywordsListsToUse.find((item) => item.id === parseInt(topicParameter))
      : keywordsListsToUse[0],
    chartDataOption = chartDataOptionSelected,
    excludeMixedAndNeutral = false,
    source,
    freeSearch,
    stakeholderId,
    sentimentDateInterval,
  }) => {
    let rawData = {};
    let finalResults = [];
    let value;

    if (chartDataOption === 'Contribution type' || chartDataOption === 'Overall') {
      let graphDataSectionsFiltered = graphDataSections.filter((item) => {
        if (item.type === 'Tweet' && !enableTwitter) {
          return false;
        }
        if (item.value === 'Edm' && !UKSelected) {
          return false;
        } else if (item.name === 'Retweets' && chartDataOption === 'Overall') {
          return false;
        } else {
          return true;
        }
      });

      for (let i = 0; i < graphDataSectionsFiltered.length; i++) {
        let section = graphDataSectionsFiltered[i];
        let results = await individualApiCall({
          topic,
          section,
          source,
          freeSearch,
          stakeholderId,
          chartDataOption,
          dateInterval: sentimentDateInterval,
        });
        if (results) {
          finalResults.push(results.values);
          rawData[section.name] = results.values;
        }
      }

      let compiledData = finalResults.reduce((acc, item) => {
        let obj = item;
        Object.keys(obj).forEach((key) => {
          if (!acc[key]) {
            acc[key] = obj[key];
          } else {
            acc[key] += obj[key];
          }
        });
        return acc;
      }, {});
      if (Object.keys(finalResults)?.length > 0) {
        value = sentiments.map((item) => compiledData[item] ?? '');
      }
    } else {
      let results = await callOverallDataChart({
        chartDataOption,
        topic,
        excludeMixedAndNeutral,
        source,
        freeSearch,
        dateInterval: sentimentDateInterval,
      });
      if (results) {
        rawData = results.rawData;
        value = results.value;
      }
    }

    if (!!value) {
      return {
        labels: sentiments.map((item) => uppercaseFirstLetter(item)),
        value,
        colours: sentiments.map((item) => colours[item]),
        rawData,
      };
    }
  };

  const sortOverallData = ({ data }) => {
    const combined = data.value.map((v, i) => ({
      value: Number(v) || 0,
      label: data.labels[i],
      colour: data.colours[i],
    }));

    combined.sort((a, b) => b.value - a.value);

    const sorted = {
      value: combined.map((item) => item.value),
      labels: combined.map((item) => item.label),
      colours: combined.map((item) => item.colour),
    };
    return sorted;
  };

  return { callOverallSentiment, sortOverallData };
};

export { colours };
export default useCallOverallSentiment;
